import type React from "react"
import Link from "next/link"

import { cn } from "@coval/ui"
import { Plus } from "lucide-react"

interface SidebarNavItemRowProps {
  href: string
  createHref?: string
  /** Only rendered alongside `createHref`. */
  createLabel?: string
  dataTestId?: string
  onMouseEnter?: () => void
  /** Intercepts the row link; may `preventDefault` to suppress navigation. */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
  active: boolean
  /** Marks the row as the current page for assistive tech. */
  isCurrentPage?: boolean
  children: React.ReactNode
}

export const SidebarNavItemRow = ({
  href,
  createHref,
  createLabel,
  dataTestId,
  onMouseEnter,
  onClick,
  active,
  isCurrentPage,
  children,
}: SidebarNavItemRowProps) => {
  const rowClasses = cn(
    "group flex items-center gap-2 px-2 py-1 transition-colors",
    active ? "bg-surface-1 rounded-sm" : "hover:bg-surface-1 hover:rounded-sm",
  )

  if (!createHref) {
    return (
      <Link
        href={href}
        data-testid={dataTestId}
        onMouseEnter={onMouseEnter}
        onClick={onClick}
        aria-current={isCurrentPage ? "page" : undefined}
        className={rowClasses}
      >
        {children}
      </Link>
    )
  }

  return (
    <div className={cn(rowClasses, "pr-1")} onMouseEnter={onMouseEnter}>
      <Link
        href={href}
        data-testid={dataTestId}
        onClick={onClick}
        className="flex items-center gap-2 min-w-0 flex-1"
      >
        {children}
      </Link>
      <Link
        href={createHref}
        aria-label={createLabel}
        className="ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-coval-text-muted opacity-0 transition-opacity hover:bg-surface-2 hover:text-coval-text group-hover:opacity-100 focus-visible:opacity-100"
      >
        <Plus className="h-3.5 w-3.5" />
      </Link>
    </div>
  )
}
